import type { IntlShape, MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import React from 'react';
import ReactDOM from 'react-dom';

import type { ExtensionManifest, ExtensionModule } from '@atlaskit/editor-common/extensions';
import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import BoardIcon from '@atlaskit/icon/core/board';
import DatabaseIcon from '@atlaskit/icon/core/database';
import GridIcon from '@atlaskit/icon/core/grid';
import TableIcon from '@atlaskit/icon/core/table';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';

import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	UNSAFE_expValEqualsNoExposure,
	UNSAFE_expValNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { DATABASE_VIEW } from '@confluence/named-routes/entry-points/routes.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getTraceIdFromApolloError } from '@confluence/template-utils/entry-points/errorUtils.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { CREATE_DATABASE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { SessionDataContext } from '@confluence/session-data/entry-points/SessionDataContext.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import CreateContentInlineMutation, {
	type CreateContentInlineMutation$data,
} from './__generated__/CreateContentInlineMutation.graphql.ts';
import { i18n } from './confluenceDatabasesExtensionI18n.ts';
import InlineCreateIcon from './icons/InlineCreateIcon.svg';
import InlineEmbedIcon from './icons/InlineEmbedIcon.svg';
import { DatabaseReferenceModal } from './components/DatabaseReferenceModal.tsx';
import { DatabaseValueContainer } from './components/DatabaseValueContainer.tsx';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
type CreateDatabaseExtensionProps = {
	pageId: string;
	spaceKey: string;
	spaceId: string;
	intl: IntlShape;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	contentType?: string;
	allowDatabaseQuickInsert?: boolean;
	isEdit?: boolean;
	pageTreeStateUpdater?: PageTreeStateUpdater;
};

type ExpectedError = {
	description: MessageDescriptor;
	title: MessageDescriptor;
} | null;

type Template = {
	layout?: 'table' | 'gallery' | 'board';
	type: string;
};
export const LS_DB_CLEANUP_TIMEOUT = 10000;
const isParentPageMovedError = (error: any) => {
	if (error && 'graphQLErrors' in error) {
		return error.graphQLErrors?.some((graphQLError: any) =>
			graphQLError.extensions.data.errors.some((nestedError: any) =>
				nestedError.message.key.includes('The parent space does not match the desired space'),
			),
		);
	}
	return false;
};

const isInvalidParentError = (error: any) => {
	if (error && 'graphQLErrors' in error) {
		return error.graphQLErrors?.some((graphQLError: any) =>
			graphQLError.extensions.data.errors.some((nestedError: any) =>
				nestedError.message.key.includes('Invalid potential parent passed'),
			),
		);
	}
	return false;
};

export const getExpectedError = (
	error: ApolloErrorInterop | Error | null | undefined,
): ExpectedError => {
	if (isParentPageMovedError(error)) {
		return {
			title: i18n.parentPageMovedErrorTitle,
			description: i18n.parentPageMovedErrorDescription,
		};
	} else if (isInvalidParentError(error)) {
		return {
			title: i18n.invalidParentErrorTitle,
			description: i18n.invalidParentErrorDescription,
		};
	} else if (isUnauthorizedError(error)) {
		return {
			title: i18n.permissionRemovedErrorTitle,
			description: i18n.permissionRemovedErrorDescription,
		};
	}
	return null;
};

const fireFlagActionClickedAnalytics = ({
	createAnalyticsEvent,
	template,
}: {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	template?: Template;
}) => {
	createAnalyticsEvent?.({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'redirectToDatabase',
			source: 'editorComponent',
			attributes: {
				flow: 'inlineDatabaseCreation',
				contentType: 'database',
				collabService: 'canvas-relay',
				templateType: template?.type,
				templateLayout: template?.layout,
			},
		},
	}).fire();
};

export const createDatabaseQuickInsertModule = ({
	pageId,
	spaceKey,
	spaceId,
	experienceTracker,
	createAnalyticsEvent,
	flags,
	template,
	pageTreeStateUpdater,
}: {
	pageId: string;
	spaceKey: string;
	spaceId: string;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	/** These correspond to template types in canvas/database **/
	template?: Template;
	pageTreeStateUpdater?: PageTreeStateUpdater;
}) => {
	return async () => {
		try {
			experienceTracker?.start({
				name: CREATE_DATABASE_EXPERIENCE,
				timeout: ExperienceTimeout.CREATE_CONTENT,
				attributes: {
					flow: 'inlinePageCreation',
				},
			});

			const mutationResult = await mutateInterop<CreateContentInlineMutation$data>({
				mutation: CreateContentInlineMutation,
				variables: { createdInContentId: pageId, spaceKey },
			});
			const contentId = mutationResult?.data?.createContentInline?.id ?? '';

			if (template) {
				// canvas/database uses this to determine which template to apply
				// this would be more ideal to handle through stream hub events during the CreateContentInlineMutation, but we needed a quick and dirty solution
				AtlBrowserStorageLocal.setItem(
					keys.DATABASES_CREATE_WITH_TEMPLATE_TYPE,
					JSON.stringify({
						templateLayout: template.layout,
						templateType: template.type,
						contentId,
					}),
				);

				// to help alleviate edge cases where user navigates away before db creation completes and canvas removes the local storage entry
				setTimeout(() => {
					AtlBrowserStorageLocal.removeItem(keys.DATABASES_CREATE_WITH_TEMPLATE_TYPE);
				}, LS_DB_CLEANUP_TIMEOUT);
			}

			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					actionSubject: 'content',
					action: 'createdByFlow',
					objectId: contentId,
					objectType: 'database',
					containerId: spaceId,
					containerType: 'space',
					source: 'editorComponent',
					attributes: {
						flow: 'inlineDatabaseCreation',
						contentType: 'database',
						collabService: 'canvas-relay',
						templateType: template?.type,
						templateLayout: template?.layout,
					},
				},
			}).fire();

			const fullUrl = getFullDatabaseUrl(contentId, spaceKey);

			await flags?.showFlag({
				type: 'success-circle',
				title: <FormattedMessage {...i18n.successFlagTitle} />,
				actions: [
					{
						content: <FormattedMessage {...i18n.successFlagAction} />,
						href: fullUrl,
						target: '_blank',
						onClick: () => fireFlagActionClickedAnalytics({ createAnalyticsEvent, template }),
					},
				],
				description: <FormattedMessage {...i18n.successFlagDescription} />,
			});

			if (contentId && pageTreeStateUpdater) {
				pageTreeStateUpdater.setForceFetchPageId(contentId);
			}

			experienceTracker?.succeed({
				name: CREATE_DATABASE_EXPERIENCE,
			});

			if (fg('platform_native_embeds_rollout_non_maui_experience')) {
				return {
					type: 'extension',
					attrs: {
						extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:database`,
						extensionType: NATIVE_EMBED_EXTENSION_TYPE,
						parameters: {
							macroParams: {
								url: { value: fullUrl },
								alwaysShowTitle: { value: 'true' },
							},
						},
					},
				};
			}

			return {
				type: 'embedCard',
				attrs: {
					url: fullUrl,
				},
			};
		} catch (error) {
			// only do this on error instead of finally, canvas/database will handle cleanup on success
			AtlBrowserStorageLocal.removeItem(keys.DATABASES_CREATE_WITH_TEMPLATE_TYPE);

			const cteTraceId = getTraceIdFromApolloError(error);
			const expectedErrorMessage = getExpectedError(error);
			if (expectedErrorMessage) {
				experienceTracker?.succeed({
					name: CREATE_DATABASE_EXPERIENCE,
					attributes: {
						abortReason: 'expectedErrorMessage',
					},
				});
				await flags?.showErrorFlag({
					title: <FormattedMessage {...expectedErrorMessage.title} />,
					description: <FormattedMessage {...expectedErrorMessage.description} />,
				});
			} else {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.DATABASES,
				});
				experienceTracker?.stopOnError({
					name: CREATE_DATABASE_EXPERIENCE,
					error,
					attributes: {
						cteTraceId,
					},
				});
				await flags?.showErrorFlag({
					title: <FormattedMessage {...i18n.defaultErrorFlagTitle} />,
					description: <FormattedMessage {...i18n.defaultErrorFlagDescription} />,
				});
			}
			markErrorAsHandled(error);
		}
	};
};

export const getFullDatabaseUrl = (contentId: string, spaceKey: string) => {
	const databaseUrl = DATABASE_VIEW.toUrl({
		spaceKey,
		contentId,
	});

	return window.location.origin + databaseUrl;
};

export const referenceDatabaseQuickInsertModule = (intl: IntlShape) => {
	return async () => {
		const el = document.createElement('div');
		document.body.appendChild(el);

		const sessionData = await getSessionData();

		let result;
		try {
			result = await new Promise((resolve) => {
				// eslint-disable-next-line react/no-deprecated
				ReactDOM.render(
					<SessionDataContext.Provider value={sessionData}>
						<DatabaseReferenceModal
							onClose={async (result: string | undefined) => {
								resolve(result);
							}}
							intl={intl}
						/>
					</SessionDataContext.Provider>,
					el,
				);
			});
		} finally {
			// eslint-disable-next-line react/no-deprecated
			ReactDOM.unmountComponentAtNode(el);
			document.body.removeChild(el);
		}

		if (result) {
			if (fg('platform_native_embeds_rollout_non_maui_experience')) {
				return {
					type: 'extension',
					attrs: {
						extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:database`,
						extensionType: NATIVE_EMBED_EXTENSION_TYPE,
						parameters: {
							macroParams: {
								url: { value: result },
								alwaysShowTitle: { value: 'true' },
							},
						},
					},
				};
			}

			return {
				type: 'embedCard',
				attrs: {
					url: result,
				},
			};
		} else {
			return;
		}
	};
};

const getDatabaseIcon = () => {
	// when cleaning up the experiment, can productionalize the DatabaseIcon
	if (
		['new-description', 'orig-description'].includes(
			UNSAFE_expValNoExposure('cc_fd_db_top_editor_toolbar', 'cohort', 'control'),
		)
	) {
		return <DatabaseIcon label="" />;
	} else if (UNSAFE_expValNoExposure('cc_fd_db_quick_insert_options', 'isEnabled', false)) {
		return <DatabaseIcon label="" />;
	} else {
		return <img src={InlineCreateIcon} alt="" />;
	}
};

const getQuickInsertItemsForPage = ({
	pageId,
	spaceKey,
	spaceId,
	experienceTracker,
	createAnalyticsEvent,
	intl,
	flags,
	pageTreeStateUpdater,
}: {
	pageId: string;
	spaceKey: string;
	spaceId: string;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	intl: IntlShape;
	pageTreeStateUpdater?: PageTreeStateUpdater;
}) => {
	const items: ExtensionModule[] = [
		{
			key: 'create-database',
			title: ['new-description', 'orig-description'].includes(
				UNSAFE_expValNoExposure('cc_fd_db_top_editor_toolbar', 'cohort', 'control'),
			)
				? intl.formatMessage(i18n.databaseDefaultTitle)
				: intl.formatMessage(i18n.createDatabaseTitle),
			description: UNSAFE_expValEqualsNoExposure(
				'cc_fd_db_top_editor_toolbar',
				'cohort',
				'new-description',
			)
				? intl.formatMessage(i18n.databaseDefaultDescription)
				: intl.formatMessage(i18n.createDatabaseDescription),
			priority: -100,
			icon: async () => () => getDatabaseIcon(),
			action: createDatabaseQuickInsertModule({
				pageId,
				spaceKey,
				spaceId,
				experienceTracker,
				createAnalyticsEvent,
				flags,
				pageTreeStateUpdater,
			}),
			...(['new-description', 'orig-description'].includes(
				UNSAFE_expValNoExposure('cc_fd_db_top_editor_toolbar', 'cohort', 'control'),
			)
				? { featured: true }
				: {}),
		},
	];

	// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
	if (UNSAFE_expValNoExposure('cc_fd_db_quick_insert_options', 'isEnabled', false)) {
		const createDatabaseProps = {
			pageId,
			spaceKey,
			spaceId,
			experienceTracker,
			createAnalyticsEvent,
			flags,
			pageTreeStateUpdater,
		};

		items.push(
			{
				key: 'create-database-board-layout',
				title: intl.formatMessage(i18n.databaseBoardLayoutTitle),
				description: intl.formatMessage(i18n.databaseBoardLayoutDescription),
				icon: async () => () => (
					<BoardIcon label={intl.formatMessage(i18n.databaseBoardLayoutIconAltText)} />
				),
				action: createDatabaseQuickInsertModule({
					...createDatabaseProps,
					template: {
						layout: 'board',
						type: 'simpleDatabase',
					},
				}),
			},
			{
				key: 'create-database-gallery-layout',
				title: intl.formatMessage(i18n.databaseGalleryLayoutTitle),
				description: intl.formatMessage(i18n.databaseGalleryLayoutDescription),
				icon: async () => () => (
					<GridIcon label={intl.formatMessage(i18n.databaseGalleryLayoutIconAltText)} />
				),
				action: createDatabaseQuickInsertModule({
					...createDatabaseProps,
					template: {
						layout: 'gallery',
						type: 'simpleDatabase',
					},
				}),
			},
			{
				key: 'create-database-table-layout',
				title: intl.formatMessage(i18n.databaseTableLayoutTitle),
				description: intl.formatMessage(i18n.databaseTableLayoutDescription),
				icon: async () => () => (
					<TableIcon label={intl.formatMessage(i18n.databaseTableLayoutIconAltText)} />
				),
				action: createDatabaseQuickInsertModule({
					...createDatabaseProps,
					template: {
						layout: 'table',
						type: 'simpleDatabase',
					},
				}),
			},
		);
	}
	return items;
};

export const createDatabaseExtension = ({
	pageId,
	spaceKey,
	spaceId,
	intl,
	flags,
	experienceTracker,
	createAnalyticsEvent,
	contentType,
	allowDatabaseQuickInsert,
	isEdit,
	pageTreeStateUpdater,
}: CreateDatabaseExtensionProps): ExtensionManifest => {
	const quickInsert: ExtensionModule[] = [];

	if (contentType === 'page') {
		quickInsert.push(
			...getQuickInsertItemsForPage({
				pageId,
				spaceKey,
				spaceId,
				experienceTracker,
				createAnalyticsEvent,
				intl,
				flags,
				pageTreeStateUpdater,
			}),
		);
	}

	quickInsert.push({
		key: 'reference-database',
		title: intl.formatMessage(i18n.referenceDatabaseTitle),
		description: intl.formatMessage(i18n.referenceDatabaseDescription),
		icon: async () => () => (
			<img src={InlineEmbedIcon} alt={intl.formatMessage(i18n.referenceDatabaseIconAltText)} />
		),
		action: referenceDatabaseQuickInsertModule(intl),
	});

	return {
		title: intl.formatMessage(i18n.manifestTitle),
		type: 'com.atlassian.confluence.macro.core',
		key: 'database-extension',
		icons: {
			'48': async () => () => getDatabaseIcon(),
		},
		modules: {
			quickInsert: allowDatabaseQuickInsert ? quickInsert : [],
			nodes: {
				default: {
					type: 'inlineExtension',
					render: async () => {
						const sessionData = await getSessionData();
						return ({ node }) => {
							const contentId = node.parameters?.macroParams?.contentId.value;
							const entryId = node.parameters?.macroParams?.entryId.value;
							const fieldId = node.parameters?.macroParams?.fieldId.value;
							return (
								<SessionDataContext.Provider value={sessionData}>
									<DatabaseValueContainer
										contentId={contentId}
										fieldId={fieldId}
										entryId={entryId}
										isEdit={isEdit}
									/>
								</SessionDataContext.Provider>
							);
						};
					},
				},
			},
		},
	};
};
